/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {useSignals} from '@preact/signals-react/runtime';

/**
 * A higher-order component (HOC) that enables reactivity for components
 * that consume `@preact/signals-react` signals by injecting `useSignals()`.
 * Only use this HOC on components that **directly read** signals via `.value`.
 * Wrapping components unnecessarily may lead to redundant re-renders.
 * ---
 * Basic usage
 * ```typescript
 * import {withSignals} from './withSignals';
 * import {counter} from './signals';
 * 
 * const CounterDisplay = () => {
 *   return <span>Value: {counter.value}</span>; // signal read
 * };
 * 
 * export default withSignals(CounterDisplay);
 * ```
 *
 * With props
 * ```typescript
 * type Props = { label: string };
 * 
 * const LabeledCounter = ({ label }: Props) => {
 *   return <div>{label}: {counter.value}</div>; // signal read
 * };
 * 
 * export default withSignals(LabeledCounter);
 * ```
 * ---
 * @template P      Props passed to the wrapped component.
 * @param Component The React component that will be wrapped with 
 *                  signal reactivity.
 * @returns         A component wrapped with `useSignals()` so it 
 *                  re-renders on signal updates.
 */
export const withSignals = <P, >(Component: React.ComponentType<P>) => {
  const WithSignals = React.forwardRef<any, P>((props, ref) => {
    useSignals();
    return React.createElement(Component as any, {...props, ref});
  });

  WithSignals.displayName = `withSignals(${Component.displayName || Component.name || 'Component'})`;

  return WithSignals;
};

export default withSignals;
